/** @format */

import {
  Box,
  Button,
  Paper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { useApiFeedback } from "../app/useApiFeedback";
import {
  fetchAllConstantsThunk,
  selectConstants,
} from "../common/dropdowns/Api/constantSlice";
import MessageResponse from "../common/feedback/messageResponse";
import SimpleBackdrop from "../common/loaders/backdrop";
import CircularLoader from "../common/loaders/circularLoader";
import { sessions } from "../common/localstorage/SessionStorage";
import {
  loginApplicantThunk,
  selectApplicant,
} from "../features/applicant/ApplicantSlice";
import "../features/applicant/applicant.css";
import Address from "../features/applicant/components/Address/Address";
import DocumentUploads from "../features/applicant/components/DocumentUploads";
import Education from "../features/applicant/components/Education/Education";
import GuardianDetails from "../features/applicant/components/Guardian/GuardianDetails";
import PersonalDetails from "../features/applicant/components/PersonalDetails/PersonalDetails";
import StudyDetails from "../features/applicant/components/StudyDetails";

const steps = [
  {
    label: "Personal Details",
    description: ``,
  },
  {
    label: "Address Details",
    description: ``,
  },
  {
    label: "Current Education Details",
    description: ``,
  },
  {
    label: "Study Details Being Applied For",
    description: ``,
  },
  {
    label: "Guardian Details",
    description: ``,
  },
  {
    label: "Document Upload",
    description: ``,
  },
];

export default function Application() {
  const { feedback, handleApiResponse } = useApiFeedback();
  const [activeStep, setActiveStep] = React.useState(0);

  const handleReset = () => {
    setActiveStep(0);
  };

  const dispatch = useAppDispatch();
  const constSelector = useAppSelector(selectConstants);
  const applicantSelector = useAppSelector(selectApplicant);
  const navigator = useNavigate();

  const renderStepperContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <PersonalDetails
            activeStep={activeStep}
            setActiveStep={setActiveStep}
          />
        );
      case 1:
        return (
          <Address activeStep={activeStep} setActiveStep={setActiveStep} />
        );

      case 2:
        return (
          <Education activeStep={activeStep} setActiveStep={setActiveStep} />
        );
      case 3:
        return (
          <StudyDetails activeStep={activeStep} setActiveStep={setActiveStep} />
        );
      case 4:
        return (
          <GuardianDetails
            activeStep={activeStep}
            setActiveStep={setActiveStep}
          />
        );
      case 5:
        return (
          <DocumentUploads
            activeStep={activeStep}
            setActiveStep={setActiveStep}
          />
        );
      default:
        break;
    }
  };

  const navigateUsingLabels = (index: number) => {
    if (
      applicantSelector.loginState.accessToken &&
      applicantSelector.loginState.accessToken.application
    ) {
      if (
        applicantSelector.loginState.accessToken.application.applicationStage >=
        index
      ) {
        setActiveStep(index);
      }
    }
  };

  const renderStepperHeader = () => {
    return (
      <Box sx={{ maxWidth: "100%" }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel
                optional={
                  index === 5 ? (
                    <Typography variant="caption">Last step</Typography>
                  ) : null
                }
              >
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    color: "#000",
                    cursor: "pointer",
                    ":hover": {
                      textDecoration: "underline",
                    },
                  }}
                  onClick={() => navigateUsingLabels(index)}
                >
                  {step.label}
                </Box>
              </StepLabel>
              <StepContent>
                <Typography mb={2}>{step.description}</Typography>

                {(constSelector.constantState.status === "idle" ||
                  applicantSelector.applicantCreateState.status === "idle" ||
                  applicantSelector.loginState.status === "idle") &&
                  renderStepperContent()}
              </StepContent>
            </Step>
          ))}
        </Stepper>
        {activeStep === steps.length && (
          <Paper square elevation={0} sx={{ p: 3 }}>
            <Typography>All steps completed - you&apos;re finished</Typography>
            <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
              Reset
            </Button>
          </Paper>
        )}
      </Box>
    );
  };

  React.useEffect(() => {
    dispatch(fetchAllConstantsThunk()).then((data: any) =>
      handleApiResponse(data, false)
    );
  }, []);

  React.useEffect(() => {
    if (sessions.getToken()) {
      dispatch(loginApplicantThunk()).then((resp: any) => {
        handleApiResponse(resp, true);
        if (resp.type.includes("fulfilled")) {
          if (
            resp.payload &&
            resp.payload.data &&
            resp.payload.data.application &&
            resp.payload.data.application?.applicationStage > 5
          ) {
            navigator("/applicant");
            return;
          }

          if (
            resp.payload &&
            resp.payload.data &&
            resp.payload.data.application
          )
            setActiveStep(resp.payload.data.application?.applicationStage);
        }
      });
    }
  }, []);

  return (
    <>
      <MessageResponse feedback={feedback} />
      {constSelector.constantState.status !== "failed" && (
        <Box mt={2}>
          {constSelector.constantState.status === "idle" &&
            renderStepperHeader()}
        </Box>
      )}

      <SimpleBackdrop status={applicantSelector.loginState.status} />
      <CircularLoader status={constSelector.constantState.status} />
    </>
  );
}
